import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Copy, Users, Link as LinkIcon, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { copyText, openExternal, composeInvite } from "../lib/clipboard";
import { api } from "../lib/api";

const AVATARS = [
  { id: "pink-w",   img: "/assets/home/avatar-pink-w.png",   ring: "#FF2E9A" },
  { id: "orange-w", img: "/assets/home/avatar-orange-w.png", ring: "#FFE082" },
  { id: "purple-w", img: "/assets/home/avatar-purple-w.png", ring: "#9B6BFF" },
  { id: "red-m",    img: "/assets/home/avatar-red-m.png",    ring: "#FF8A00" },
  { id: "blue-m",   img: "/assets/home/avatar-blue-m.png",   ring: "#3DA4FF" },
  { id: "green-m",  img: "/assets/home/avatar-green-m.png",  ring: "#3DD68C" },
];

function FloatingParty({ count = 14 }) {
  const items = Array.from({ length: count }, (_, i) => ({
    left: (i * 17 + 5) % 96,
    top:  (i * 13 + 8) % 86,
    size: 12 + (i % 4) * 4,
    delay: (i * 0.4) % 7,
    dur: 4 + (i % 5) * 0.6,
    emoji: ["🎉", "🔥", "✨", "💫", "😈"][i % 5],
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {items.map((p, i) => (
        <motion.span
          key={i}
          className="absolute"
          style={{ left: `${p.left}%`, top: `${p.top}%`, fontSize: p.size, filter: "drop-shadow(0 0 6px rgba(255, 138, 0, 0.6))" }}
          animate={{ y: [-2, -14, -2], opacity: [0, 0.6, 0.6, 0], rotate: [-12, 12, -12] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  );
}

/**
 * Screen 2 — Create Group room.
 * Reference mimicked:
 *  - "Your private room is ready!" gradient title
 *  - Hovering heart+people icon hero
 *  - VIBE-XXXX code pill with tap-to-copy
 *  - Big "Share Invite Link" pink CTA + outline "Invite Friends" (Share API)
 *  - Helper: "The more people join… the worse it gets 😈"
 */
export default function PlayGroupCreate({ onBack, onReady }) {
  const [step, setStep] = useState("setup");   // 'setup' | 'ready'
  const [hostName, setHostName] = useState(localStorage.getItem("player_name") || "");
  const [avatarId, setAvatarId] = useState(AVATARS[Math.floor(Math.random() * AVATARS.length)].id);
  const [creating, setCreating] = useState(false);
  const [room, setRoom] = useState(null);

  const handleCreate = async () => {
    if (!hostName.trim()) {
      toast.error("What should we call you?");
      return;
    }
    setCreating(true);
    try {
      const r = await api.createGroupRoom({ host_name: hostName.trim(), avatar_id: avatarId });
      api.streakPing().catch(() => { /* offline tolerant */ });
      localStorage.setItem("player_name", hostName.trim());
      localStorage.setItem("group_avatar_id", avatarId);
      localStorage.setItem(`group_host_${r.code}`, "1");
      setRoom(r);
      setStep("ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create room");
    } finally {
      setCreating(false);
    }
  };

  const joinUrl = room ? `${window.location.origin}/group-join/${room.code}` : "";
  const inviteText = room ? `${hostName.trim()} started a Group Mode session on VibeCheck 🎉 — join: ${joinUrl}` : "";

  const copyLink = async () => {
    const ok = await copyText(joinUrl);
    if (ok) toast.success("Link copied 💌");
  };
  const shareInvite = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "VibeCheck — Group Mode", text: inviteText, url: joinUrl });
      } else {
        await openExternal(`https://wa.me/?text=${encodeURIComponent(inviteText)}`);
      }
    } catch { /* user cancelled */ }
  };
  const copyCode = async () => {
    const ok = await copyText(room.code);
    if (ok) toast.success(`Room code ${room.code} copied`);
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-create-screen">
      {/* Warm bloom */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 40, left: "50%", transform: "translateX(-50%)",
          width: 520, height: 520,
          background: "radial-gradient(circle, rgba(255, 138, 0, 0.35) 0%, rgba(255, 184, 0, 0.16) 38%, transparent 70%)",
          filter: "blur(48px)", zIndex: 0,
        }}
        aria-hidden
      />
      <FloatingParty count={14} />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="group-create-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div
            className="px-3 py-1 rounded-full text-[10px] font-black tracking-[0.22em]"
            style={{
              background: "rgba(255, 138, 0, 0.10)",
              border: "1.5px solid #FF8A00",
              color: "#FFE082",
              boxShadow: "0 0 10px rgba(255, 138, 0, 0.45)",
            }}
          >
            🎉 GROUP MODE
          </div>
          <div className="w-10" aria-hidden />
        </div>

        {step === "setup" && (
          <>
            <div className="text-center mb-5 mt-4">
              <h1
                className="font-extrabold leading-[0.95] tracking-tight mb-2"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "clamp(2.0rem, 8.5vw, 2.6rem)",
                  letterSpacing: "-0.03em",
                  background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
                  filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
                }}
                data-testid="group-create-title"
              >
                Start a Group room
              </h1>
              <p className="text-white/75 text-[14px] font-medium">
                You&rsquo;re the host. Pick your look, name yourself, and we&rsquo;ll mint a code.
              </p>
            </div>

            {/* Avatar picker */}
            <div className="text-center mb-3">
              <div className="text-[10.5px] font-black tracking-[0.22em] text-[#FFE082] mb-2.5">CHOOSE YOUR LOOK</div>
              <div className="grid grid-cols-6 gap-2.5 px-1 mb-1" data-testid="group-create-avatars">
                {AVATARS.map((a) => {
                  const sel = avatarId === a.id;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setAvatarId(a.id)}
                      data-testid={`group-create-avatar-${a.id}`}
                      className="aspect-square rounded-full overflow-hidden transition active:scale-95"
                      style={{
                        border: `2.5px solid ${sel ? a.ring : "rgba(255, 255, 255, 0.10)"}`,
                        boxShadow: sel
                          ? `0 0 16px ${a.ring}aa, inset 0 0 8px ${a.ring}33`
                          : "none",
                      }}
                    >
                      <img src={a.img} alt="" className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name input */}
            <div className="mt-2">
              <label className="block text-[10.5px] font-black tracking-[0.22em] text-[#FFE082] mb-2 pl-1">
                YOUR NAME
              </label>
              <input
                value={hostName}
                onChange={(e) => setHostName(e.target.value)}
                placeholder="Sam"
                maxLength={20}
                data-testid="group-create-name-input"
                className="w-full rounded-2xl px-5 py-[14px] text-white text-[16px] font-bold placeholder:text-white/35 focus:outline-none"
                style={{
                  background: "rgba(15, 8, 22, 0.55)",
                  border: "1.5px solid #FF8A00",
                  boxShadow: "0 0 14px rgba(255, 138, 0, 0.4)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              />
            </div>

            <div className="flex-1" />

            <button
              onClick={handleCreate}
              disabled={creating}
              data-testid="group-create-mint"
              className="vc-cta vc-cta-primary w-full rounded-full py-[18px] font-extrabold flex items-center justify-center gap-2"
              style={{ fontSize: "17px" }}
            >
              {creating ? "Minting room…" : (<>Mint Room <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
            </button>
          </>
        )}

        {step === "ready" && room && (
          <>
            <div className="text-center mb-3 mt-3">
              <h1
                className="font-extrabold leading-[0.95] tracking-tight mb-2"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "clamp(2.0rem, 8.5vw, 2.6rem)",
                  letterSpacing: "-0.03em",
                  background: "linear-gradient(90deg, #FFF3C2 0%, #FFB800 55%, #FF8A00 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 0 1px rgba(255, 184, 0, 0.6)",
                  filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
                }}
                data-testid="group-create-ready-title"
              >
                Your private room
                <br />is ready! <span style={{ filter: "drop-shadow(0 0 8px #FF8A00)" }}>🎉</span>
              </h1>
              <p className="text-white/75 text-[13.5px] font-medium">
                Share the code or link. The more people who join,
                <br />the worse it gets <span style={{ filter: "drop-shadow(0 0 6px #FF8A00)" }}>😈</span>
              </p>
            </div>

            {/* Hovering hero icon — heart with people */}
            <div className="flex justify-center my-3">
              <motion.div
                animate={{ y: [0, -6, 0], rotate: [-1.5, 1.5, -1.5] }}
                transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-[150px] h-[150px] flex items-center justify-center"
                style={{ filter: "drop-shadow(0 0 30px rgba(255, 138, 0, 0.7)) drop-shadow(0 0 60px rgba(255, 184, 0, 0.35))" }}
              >
                {/* Halo */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{ background: "radial-gradient(circle, rgba(255, 138, 0, 0.4) 0%, transparent 65%)", filter: "blur(12px)" }}
                  animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  aria-hidden
                />
                {/* Heart shape */}
                <svg width="138" height="138" viewBox="0 0 24 24" className="relative">
                  <defs>
                    <linearGradient id="grpHeartGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0" stopColor="#FFE082" />
                      <stop offset="0.5" stopColor="#FF8A00" />
                      <stop offset="1" stopColor="#FF8A00" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M12 21s-7-4.5-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.5-7 10-7 10z"
                    fill="url(#grpHeartGrad)"
                    stroke="rgba(255, 255, 255, 0.4)"
                    strokeWidth="0.5"
                  />
                </svg>
                {/* People icon centered */}
                <Users className="absolute w-[50px] h-[50px]" style={{ color: "white", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))" }} strokeWidth={2.2} />
              </motion.div>
            </div>

            {/* Code pill */}
            <div className="text-center mb-4">
              <div className="text-[10.5px] font-black tracking-[0.32em] text-[#FFE082] mb-2">ROOM CODE</div>
              <button
                onClick={copyCode}
                data-testid="group-create-code-pill"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-extrabold text-white transition active:scale-[0.98]"
                style={{
                  background: "rgba(15, 8, 22, 0.7)",
                  border: "2px solid #FF8A00",
                  boxShadow: "0 0 24px rgba(255, 138, 0, 0.65)",
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "22px",
                  letterSpacing: "0.10em",
                }}
                aria-label={`Copy room code ${room.code}`}
              >
                {room.code}
                <Copy className="w-4 h-4 ml-1" style={{ color: "#FFE082" }} strokeWidth={2.4} />
              </button>
            </div>

            {/* CTAs */}
            <div className="space-y-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={copyLink}
                data-testid="group-create-copy-link"
                animate={{ boxShadow: [
                  "0 0 22px rgba(255, 138, 0, 0.6), 0 8px 30px rgba(255, 184, 0, 0.35)",
                  "0 0 36px rgba(255, 138, 0, 0.95), 0 10px 40px rgba(255, 184, 0, 0.55)",
                  "0 0 22px rgba(255, 138, 0, 0.6), 0 8px 30px rgba(255, 184, 0, 0.35)",
                ] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="w-full rounded-full py-[16px] font-extrabold text-white flex items-center justify-center gap-2 transition"
                style={{
                  background: "linear-gradient(180deg, #FFB800 0%, #FF8A00 100%)",
                  border: "1.5px solid rgba(255, 255, 255, 0.22)",
                  fontSize: "16px",
                }}
              >
                <LinkIcon className="w-5 h-5" strokeWidth={2.4} />
                Share Invite Link
              </motion.button>

              <button
                onClick={shareInvite}
                data-testid="group-create-share"
                className="w-full rounded-full py-[14px] font-extrabold text-white flex items-center justify-center gap-2 transition active:scale-[0.98]"
                style={{
                  background: "rgba(15, 8, 22, 0.55)",
                  border: "1.5px solid #FF8A00",
                  boxShadow: "0 0 12px rgba(255, 138, 0, 0.4)",
                  fontSize: "15px",
                }}
              >
                <Users className="w-4 h-4" style={{ color: "#FFE082" }} strokeWidth={2.4} />
                Invite Friends
              </button>
            </div>

            <div className="flex-1 min-h-[12px]" />

            {/* Continue to lobby — primary CTA */}
            <button
              onClick={() => onReady?.(room)}
              data-testid="group-create-continue-lobby"
              className="vc-cta vc-cta-primary w-full rounded-full py-[14px] font-extrabold flex items-center justify-center gap-2 mt-3"
              style={{ fontSize: "14px" }}
            >
              Go to Squad lobby
              <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
            </button>

            <div className="flex items-center justify-center gap-1.5 text-white/55 text-[11.5px] font-medium mt-3">
              <Lock className="w-3.5 h-3.5" />
              Private room · only friends with the link can join
              <Sparkles className="w-3 h-3" style={{ color: "#FFE082" }} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
